/**
 * useBootstrap Hook
 *
 * Fetches bootstrap data after authentication to determine
 * where to route the user.
 */

import { useQuery } from '@tanstack/react-query';
import { bootstrap } from '@/shared/api/bootstrap';
import type { BootstrapResponse } from '@/shared/api/bootstrap';
import { logger } from '@/shared/utils/logger';

export interface UseBootstrapOptions {
  enabled?: boolean;
  accessToken?: string | null;
}

export interface UseBootstrapReturn {
  data: BootstrapResponse | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
}

export const bootstrapKeys = {
  all: ['bootstrap'] as const,
  data: () => [...bootstrapKeys.all, 'data'] as const,
  soloStatus: () => [...bootstrapKeys.all, 'solo-status'] as const,
  shopStatus: (slug: string) => [...bootstrapKeys.all, 'shop-status', slug] as const,
};

/**
 * Hook to fetch bootstrap data for post-login routing decisions
 */
export const useBootstrap = (options: UseBootstrapOptions = {}): UseBootstrapReturn => {
  const { enabled = true, accessToken } = options;

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery<BootstrapResponse>({
    queryKey: bootstrapKeys.data(),
    queryFn: async () => {
      logger.info('Fetching bootstrap data for routing decision');
      const result = await bootstrap.getBootstrapData(accessToken);
      logger.info('Bootstrap data received', {
        userType: result.user.type,
        hasProviderProfile: result.providerProfile.exists,
        providerRole: result.providerProfile.role,
        setupComplete: result.providerProfile.setupComplete,
        shopCount: result.shops.length,
      });
      return result;
    },
    enabled,
    staleTime: 30 * 1000, // 30 seconds - short stale time for fresh routing decisions
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  });

  return {
    data: data || null,
    isLoading,
    error: error as Error | null,
    refetch,
  };
};
